"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { router, usePage } from "@inertiajs/react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"

export type DestinationCategory = {
  id: number
  label: string
  count: number
}

type Props = {
  destinations: DestinationCategory[]
}

type Option = {
  id: number | null
  label: string
  count?: number
}

export function DestinationSelect({ destinations }: Props) {
  const { url } = usePage()

  const params = React.useMemo(
    () => new URLSearchParams(url.split("?")[1]),
    [url]
  )

  const initialValue = params.get("destination")
    ? Number(params.get("destination"))
    : null

  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState<number | null>(initialValue)

  const options: Option[] = [
    { id: null, label: "All Numbers" },
    ...destinations,
  ]

  const selected = options.find(o => o.id === value)


  const navigate = (id: number | null) => {
  const params = new URLSearchParams(window.location.search)

  if (id === null) {
    params.delete("destination")
  } else {
    params.set("destination", String(id))
  }

  router.get(
    window.location.pathname,
    Object.fromEntries(params.entries()),
    {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    }
  )
}

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[250px] my-4 justify-between"
        >
          {selected?.label ?? "All Numbers"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search destination..." />
          <CommandEmpty>No destination found.</CommandEmpty>

          <CommandGroup>
            {options.map((o) => (
              <CommandItem
                key={String(o.id)}
                value={o.label}
                onSelect={() => {
                  setValue(o.id)
                  setOpen(false)
                  navigate(o.id)
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === o.id ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="flex-1">{o.label}</span>

                {o.count !== undefined && (
                  <span className="text-muted-foreground text-xs">
                    {o.count}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
